<template>
  <div ref="root" class="split-bars">
    <div class="split-bars__plot" @mouseleave="hovered = null">
      <svg v-if="backdropShape" class="split-bars__backdrop" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <path :d="backdropShape" />
      </svg>
      <button
        v-for="bar in bars"
        :key="bar.index"
        type="button"
        class="split-bars__column"
        :class="{ 'split-bars__column--active': bar.index === active }"
        :style="{ left: `${bar.left}%`, width: `${bar.width}%`, '--bar-gap': `${gap}px` }"
        :aria-label="bar.detail.title"
        @mouseenter="hovered = bar.index"
        @focus="hovered = bar.index"
        @blur="hovered = null"
        @click="emit('select', bar.index)"
      >
        <span v-if="bar.label" class="split-bars__label" :class="bar.labelClass">{{ bar.label }}</span>
        <span
          v-if="bar.fraction !== null"
          class="split-bars__bar"
          :style="{ height: `calc((100% - ${LABEL_SPACE}px) * ${bar.fraction})`, background: bar.color, opacity: bar.opacity }"
        ></span>
      </button>
      <div v-if="tooltip" class="split-bars__tooltip" :class="`split-bars__tooltip--${tooltip.side}`" :style="{ left: `${tooltip.at}%` }">
        <strong class="d-block">{{ tooltip.detail.title }}</strong>
        <span class="d-block">{{ tooltip.detail.pace }}</span>
        <span v-for="line in tooltip.detail.lines" :key="line" class="d-block text-muted">{{ line }}</span>
      </div>
    </div>
    <div class="split-bars__axis">
      <span v-for="label in axisLabels" :key="label.text" :style="{ left: `${label.left * 100}%` }">{{ label.text }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import { MINIMUM_ELEVATION_SPREAD_METERS, paceScale } from '../../../utils/recordingActivity/colorScale';
import { paceValue } from '../../../utils/recordingActivity/format';
import {
  backdropPath,
  barFraction,
  barLabelsFit,
  LABEL_CHARACTER_PX,
  scaleColor,
  splitAxisLabels,
  splitDetail,
  type PaceSplits,
} from '../../../utils/recordingActivity/paceSplits';
import type { ProfileSample } from '../../../utils/recordingActivity/profiles';

const props = defineProps<{
  splits: PaceSplits;
  pref: MeasurementPrefUtility;
  /** The elevation profile drawn behind the bars. */
  backdrop?: ProfileSample[] | null;
  /** The picked split, drawn full while the rest dim; without one every bar is full until one is hovered. */
  selected?: number | null;
}>();

const emit = defineEmits<{ (e: 'select', index: number): void }>();

const LABEL_SPACE = 22;
const BAR_GAP_PX = 10;
const FALLBACK_WIDTH_PX = 640;

const { t } = useI18n();
const root = ref<HTMLElement | null>(null);
const width = ref(FALLBACK_WIDTH_PX);
const hovered = ref<number | null>(null);
let observer: ResizeObserver | null = null;

onMounted(() => {
  if (!root.value) return;
  width.value = root.value.clientWidth || FALLBACK_WIDTH_PX;
  if (typeof ResizeObserver === 'undefined') return;
  observer = new ResizeObserver(([entry]) => {
    if (entry.contentRect.width > 0) width.value = entry.contentRect.width;
  });
  observer.observe(root.value);
});
onBeforeUnmount(() => observer?.disconnect());

const active = computed(() => hovered.value ?? props.selected ?? null);
const gap = computed(() => Math.min(BAR_GAP_PX, (props.splits.lengthMeters / props.splits.totalMeters) * width.value * 0.2));
const backdropShape = computed(() => backdropPath(props.backdrop, props.splits.totalMeters, MINIMUM_ELEVATION_SPREAD_METERS));
const axisLabels = computed(() => splitAxisLabels(props.splits, props.pref, width.value));

const bars = computed(() => {
  const { splits, totalMeters, fastestIndex, slowestIndex } = props.splits;
  const scale = paceScale(splits.flatMap((split) => (split.secondsPerMeter === null ? [] : [split.secondsPerMeter])));
  const labels = splits.map((split) => (split.secondsPerMeter === null ? null : paceValue(split.secondsPerMeter, props.pref)));
  const isLabellingEvery = barLabelsFit(props.splits, labels, width.value);
  return splits.map((split, index) => {
    const isExtreme = index === fastestIndex || index === slowestIndex;
    const columnPx = ((split.endMeters - split.startMeters) / totalMeters) * width.value;
    // A short last split has no room for its time beside its neighbour's.
    const fits = isLabellingEvery && columnPx >= (labels[index]?.length ?? 0) * LABEL_CHARACTER_PX;
    return {
      index,
      left: (split.startMeters / totalMeters) * 100,
      width: ((split.endMeters - split.startMeters) / totalMeters) * 100,
      fraction: barFraction(props.splits, index),
      color: scale && split.secondsPerMeter !== null ? scaleColor(scale, split.secondsPerMeter) : scale?.highColor ?? '#0B3D91',
      opacity: active.value === null || active.value === index ? 1 : 0.6,
      label: isExtreme || fits ? labels[index] : null,
      labelClass: {
        'split-bars__label--active': index === active.value,
        'split-bars__label--fastest': index === fastestIndex && index !== active.value,
      },
      detail: splitDetail(props.splits, index, props.pref, t),
    };
  });
});

const tooltip = computed(() => {
  if (hovered.value === null) return null;
  const bar = bars.value[hovered.value];
  if (!bar) return null;
  const at = bar.left + bar.width / 2;
  return { detail: bar.detail, at, side: at < 20 ? 'start' : at > 80 ? 'end' : 'middle' };
});
</script>

<style scoped>
.split-bars__plot {
  position: relative;
  height: 220px;
}

@media (max-width: 575.98px) {
  .split-bars__plot {
    height: 180px;
  }
}

.split-bars__backdrop {
  position: absolute;
  inset: 22px 0 0;
  width: 100%;
  height: calc(100% - 22px);
}

.split-bars__backdrop path {
  fill: rgba(255, 255, 255, 0.07);
}

.split-bars__column {
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: stretch;
  padding: 0 calc(var(--bar-gap) / 2);
  background: transparent;
  border: 0;
  cursor: pointer;
  /* MapButtons leaks a global `button { white-space: nowrap }` onto the page. */
  white-space: nowrap;
}

.split-bars__column:focus-visible {
  outline: 1px solid rgba(255, 255, 255, 0.4);
  outline-offset: -1px;
}

.split-bars__bar {
  display: block;
  min-height: 2px;
  border-radius: 3px 3px 0 0;
  transition: opacity 0.15s;
}

.split-bars__label {
  align-self: center;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-muted, #b2b2b2);
}

.split-bars__label--fastest {
  color: #ffdf46;
}

.split-bars__label--active {
  color: #fff;
}

.split-bars__tooltip {
  position: absolute;
  top: 0;
  z-index: 2;
  min-width: 140px;
  padding: 8px 10px;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #fff;
  background: rgba(40, 42, 50, 0.95);
  border-radius: 6px;
  pointer-events: none;
  transform: translateX(-50%);
}

.split-bars__tooltip--start {
  transform: none;
}

.split-bars__tooltip--end {
  transform: translateX(-100%);
}

.split-bars__axis {
  position: relative;
  height: 1.75rem;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 0.8rem;
  color: var(--text-muted, #b2b2b2);
}

.split-bars__axis span {
  position: absolute;
  white-space: nowrap;
}
</style>
